import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, ChevronDown, Clock, Info, Package, Search, Zap } from "lucide-react";
import "./simulador.css";

// Mesmos destinos que /api/public-quote reconhece (DEST_MAP do back-exporta).
// Vai o código ISO, que o back aceita direto, e o nome só serve para exibir.
// Os mais usados primeiro; o resto em ordem alfabética.
const POPULARES = ["US", "PT", "GB", "CA", "FR", "DE", "ES", "IT", "AU", "JP"];
const PAISES: Record<string, string> = {
  US: "Estados Unidos", PT: "Portugal", GB: "Reino Unido", CA: "Canadá", FR: "França",
  DE: "Alemanha", ES: "Espanha", IT: "Itália", AU: "Austrália", JP: "Japão",
  AE: "Emirados Árabes Unidos", ZA: "África do Sul", SA: "Arábia Saudita", AR: "Argentina",
  AT: "Áustria", BE: "Bélgica", BO: "Bolívia", CL: "Chile", CN: "China", SG: "Cingapura",
  CO: "Colômbia", KR: "Coreia do Sul", CR: "Costa Rica", CU: "Cuba", DK: "Dinamarca",
  EG: "Egito", EC: "Equador", PH: "Filipinas", FI: "Finlândia", GR: "Grécia",
  HK: "Hong Kong", HU: "Hungria", IN: "Índia", ID: "Indonésia", IL: "Israel",
  JM: "Jamaica", MY: "Malásia", MA: "Marrocos", MX: "México", NO: "Noruega",
  NZ: "Nova Zelândia", NL: "Países Baixos", PA: "Panamá", PY: "Paraguai", PE: "Peru",
  PL: "Polônia", CZ: "República Tcheca", RO: "Romênia", RU: "Rússia", SE: "Suécia",
  CH: "Suíça", TH: "Tailândia", TW: "Taiwan", TR: "Turquia", UY: "Uruguai", VE: "Venezuela",
};

const sem = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

const LISTA = [
  ...POPULARES,
  ...Object.keys(PAISES)
    .filter((c) => !POPULARES.includes(c))
    .sort((a, b) => PAISES[a].localeCompare(PAISES[b], "pt-BR")),
].map((iso) => ({ iso, nome: PAISES[iso] }));

// Atalhos de pacote: a maior parte de quem testa não sabe de cabeça as medidas.
const PACOTES = [
  { id: "envelope", nome: "Envelope", peso: "0.5", c: "30", l: "22", a: "3" },
  { id: "p", nome: "Caixa P", peso: "1", c: "20", l: "15", a: "10" },
  { id: "m", nome: "Caixa M", peso: "3", c: "30", l: "25", a: "20" },
  { id: "g", nome: "Caixa G", peso: "8", c: "50", l: "40", a: "30" },
];

// Mesmos ids de CATEGORIAS em services/impostosPublicos.js do back-exporta.
// Lista fechada de propósito: cada categoria nova custa uma classificação por
// IA, texto livre numa página aberta seria uma chamada paga por visitante.
const CATEGORIAS = [
  { id: "roupas", nome: "Roupas em geral" },
  { id: "fitness", nome: "Roupas fitness" },
  { id: "calcados", nome: "Calçados" },
  { id: "bolsas", nome: "Bolsas e acessórios de couro" },
  { id: "bijuterias", nome: "Bijuterias" },
  { id: "cosmeticos", nome: "Cosméticos e skincare" },
  { id: "suplementos", nome: "Suplementos alimentares" },
  { id: "cafe", nome: "Café" },
  { id: "livros", nome: "Livros" },
];

const LOGOS: Record<string, { src: string; classe: string }> = {
  fedex: { src: "/images/fedex-logo.png", classe: "simx-logo--fedex" },
  ups: { src: "/images/UPS-Loo.png", classe: "simx-logo--ups" },
};

type Opcao = {
  carrier: string;
  service?: string;
  price: number;
  published?: number;
  sla_days?: number;
  desembaraco_usd?: number;
};

type Imposto =
  | { status: "off" | "loading" | "indisponivel" | "erro" }
  | { status: "ok"; valor: number; aliquota: number; deMinimis: boolean };

const usd = (v: number) =>
  `US$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const num = (s: string) => Number(String(s).replace(",", "."));

function Bandeira({ iso }: { iso: string }) {
  const [falhou, setFalhou] = useState(false);
  if (falhou) return <span className="simx-flag simx-flag--txt">{iso}</span>;
  return (
    <img
      className="simx-flag"
      src={`https://flagcdn.com/w40/${iso.toLowerCase()}.png`}
      alt=""
      loading="lazy"
      onError={() => setFalhou(true)}
    />
  );
}

function SeletorPais({ valor, onChange }: { valor: string; onChange: (iso: string) => void }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const [ativo, setAtivo] = useState(0);
  const caixa = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);

  const filtrados = useMemo(() => {
    const q = sem(busca.trim());
    return q ? LISTA.filter((p) => sem(p.nome).includes(q) || p.iso.toLowerCase() === q) : LISTA;
  }, [busca]);

  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  useEffect(() => setAtivo(0), [busca]);

  const escolher = (iso: string) => {
    onChange(iso);
    setBusca("");
    setAberto(false);
  };

  const teclado = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAberto(true);
      setAtivo((i) => Math.min(i + 1, filtrados.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAtivo((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && aberto) {
      e.preventDefault();
      if (filtrados[ativo]) escolher(filtrados[ativo].iso);
    } else if (e.key === "Escape") {
      setAberto(false);
    }
  };

  return (
    <div className="simx-combo" ref={caixa}>
      <button
        type="button"
        className={`simx-input simx-combo__trigger ${aberto ? "is-open" : ""}`}
        onClick={() => {
          setAberto((v) => !v);
          setTimeout(() => campo.current?.focus(), 0);
        }}
        aria-haspopup="listbox"
        aria-expanded={aberto}
      >
        {valor ? (
          <span className="simx-combo__value">
            <Bandeira iso={valor} />
            {PAISES[valor]}
          </span>
        ) : (
          <span className="simx-placeholder">Para onde vai o envio?</span>
        )}
        <ChevronDown size={18} className="simx-combo__chevron" />
      </button>

      {aberto && (
        <div className="simx-combo__panel">
          <div className="simx-combo__search">
            <Search size={16} />
            <input
              ref={campo}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={teclado}
              placeholder="Buscar país"
              aria-label="Buscar país"
            />
          </div>
          <ul className="simx-combo__list" role="listbox">
            {filtrados.length === 0 && <li className="simx-combo__empty">Nenhum país encontrado</li>}
            {filtrados.map((p, i) => (
              <li
                key={p.iso}
                role="option"
                aria-selected={valor === p.iso}
                className={`simx-combo__opt ${i === ativo ? "is-active" : ""}`}
                onMouseEnter={() => setAtivo(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  escolher(p.iso);
                }}
              >
                <Bandeira iso={p.iso} />
                <span>{p.nome}</span>
                {valor === p.iso && <Check size={16} className="simx-combo__check" />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CampoUnidade({
  valor, setValor, unidade, placeholder, rotulo,
}: {
  valor: string; setValor: (v: string) => void; unidade: string; placeholder: string; rotulo: string;
}) {
  return (
    <label className="simx-unit">
      <input
        className="simx-input"
        type="number"
        inputMode="decimal"
        min="0"
        step="0.1"
        value={valor}
        placeholder={placeholder}
        aria-label={rotulo}
        onChange={(e) => setValor(e.target.value)}
      />
      <span className="simx-unit__suffix">{unidade}</span>
    </label>
  );
}

type Props = {
  onContato: (local: string) => void;
  whatsappLink: string;
};

export default function SimuladorFrete({ onContato, whatsappLink }: Props) {
  const [destino, setDestino] = useState("");
  const [peso, setPeso] = useState("");
  const [comp, setComp] = useState("");
  const [larg, setLarg] = useState("");
  const [alt, setAlt] = useState("");
  const [loading, setLoading] = useState(false);
  const [opcoes, setOpcoes] = useState<Opcao[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [categoria, setCategoria] = useState("");
  const [valorMerc, setValorMerc] = useState("");
  const [imposto, setImposto] = useState<Imposto>({ status: "off" });
  // A primeira estimativa de um produto pode levar quase um minuto: se a
  // pessoa simular de novo nesse meio tempo, a resposta antiga é descartada.
  const rodada = useRef(0);

  const pacoteAtivo = PACOTES.find(
    (p) => p.peso === peso && p.c === comp && p.l === larg && p.a === alt,
  )?.id;

  // Mesma regra do back (divisor 5000): se a caixa for leve e grande, a
  // transportadora cobra pelo volume. Avisar antes evita "por que deu tanto?".
  const cubado = num(comp) * num(larg) * num(alt) / 5000;
  const pesoCobrado = Math.max(num(peso) || 0, cubado || 0);
  const cobraPorVolume = cubado > 0 && num(peso) > 0 && cubado > num(peso);

  const podeSimular = !!destino && num(peso) > 0 && !loading;

  const simular = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!podeSimular) return;

    setLoading(true);
    setErro(null);
    setOpcoes(null);

    const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": process.env.REACT_APP_PUBLIC_QUOTE_API_KEY || "",
    };

    // Imposto corre em paralelo e não segura o frete.
    const minhaRodada = ++rodada.current;
    if (categoria && num(valorMerc) > 0) {
      setImposto({ status: "loading" });
      fetch(`${apiBase}/api/public-quote/impostos`, {
        method: "POST",
        headers,
        body: JSON.stringify({ destino, categoria, valor: valorMerc }),
      })
        .then((r) => r.json())
        .then((d) => {
          if (rodada.current !== minhaRodada) return;
          if (!d.ok) setImposto({ status: "erro" });
          else if (!d.disponivel) setImposto({ status: "indisponivel" });
          else
            setImposto({
              status: "ok",
              valor: Number(d.imposto_usd) || 0,
              aliquota: Number(d.aliquota_pct) || 0,
              deMinimis: !!d.de_minimis,
            });
        })
        .catch(() => {
          if (rodada.current === minhaRodada) setImposto({ status: "erro" });
        });
    } else {
      setImposto({ status: "off" });
    }

    try {
      const resp = await fetch(`${apiBase}/api/public-quote`, {
        method: "POST",
        headers,
        body: JSON.stringify({ comprimento: comp, largura: larg, altura: alt, peso, destino }),
      });
      const data = await resp.json();

      if (!resp.ok || !data.ok) {
        setErro(data.error || "Não foi possível calcular o frete. Tente novamente.");
        return;
      }
      setOpcoes((data.options || []).filter((o: Opcao) => Number(o.price) > 0));
    } catch {
      setErro("Não conseguimos falar com o servidor. Verifique sua conexão e tente de novo.");
    } finally {
      setLoading(false);
    }
  };

  // O que a fatura cobra: frete + desembaraço da transportadora + imposto
  // estimado (quando o visitante informou o produto). Mostrar só o frete fazia
  // a fatura parecer mais cara do que o simulado.
  const impostoUsd = imposto.status === "ok" ? imposto.valor : 0;
  const extras = (o: Opcao) => (Number(o.desembaraco_usd) || 0) + impostoUsd;
  const totalDe = (o: Opcao) => o.price + extras(o);

  const maisBarato = opcoes?.length ? Math.min(...opcoes.map(totalDe)) : null;
  const maisRapido = opcoes?.length
    ? Math.min(...opcoes.map((o) => o.sla_days ?? Infinity))
    : null;
  const melhorEconomia = opcoes?.reduce((m, o) => {
    const pub = o.published ?? o.price;
    return pub > o.price ? Math.max(m, Math.round((1 - o.price / pub) * 100)) : m;
  }, 0);

  return (
    <section className="simx" id="simulacao">
      <div className="simx__glow" aria-hidden />
      <div className="simx__container">
        <header className="simx__header">
          <span className="simx__kicker">
            <Zap size={14} /> Simulador gratuito · sem cadastro
          </span>
          <h2 className="simx__title">
            Quanto custa enviar <span>para o mundo?</span>
          </h2>
          <p className="simx__subtitle">
            Compare FedEx e UPS com as tarifas negociadas da Intrex em segundos.
          </p>
        </header>

        <div className="simx__card">
          <form className="simx__form" onSubmit={simular}>
            <div className="simx__field">
              <span className="simx__label">Destino</span>
              <SeletorPais valor={destino} onChange={setDestino} />
              <span className="simx__hint">Saindo do Brasil</span>
            </div>

            <div className="simx__field">
              <span className="simx__label">Pacote</span>
              <div className="simx__chips">
                {PACOTES.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`simx-chip ${pacoteAtivo === p.id ? "is-active" : ""}`}
                    onClick={() => {
                      setPeso(p.peso);
                      setComp(p.c);
                      setLarg(p.l);
                      setAlt(p.a);
                    }}
                  >
                    {p.nome}
                    <small>{p.peso.replace(".", ",")} kg</small>
                  </button>
                ))}
              </div>
            </div>

            <div className="simx__row">
              <div className="simx__field simx__field--peso">
                <span className="simx__label">Peso</span>
                <CampoUnidade valor={peso} setValor={setPeso} unidade="kg" placeholder="0,0" rotulo="Peso em kg" />
              </div>
              <div className="simx__field">
                <span className="simx__label">
                  Dimensões <em>opcional</em>
                </span>
                <div className="simx__dims">
                  <CampoUnidade valor={comp} setValor={setComp} unidade="cm" placeholder="C" rotulo="Comprimento em cm" />
                  <CampoUnidade valor={larg} setValor={setLarg} unidade="cm" placeholder="L" rotulo="Largura em cm" />
                  <CampoUnidade valor={alt} setValor={setAlt} unidade="cm" placeholder="A" rotulo="Altura em cm" />
                </div>
              </div>
            </div>

            <div className="simx__row simx__row--imposto">
              <div className="simx__field">
                <span className="simx__label">
                  Produto <em>para calcular impostos</em>
                </span>
                <select
                  className={`simx-input simx-select ${categoria ? "" : "is-empty"}`}
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value)}
                  aria-label="Tipo de produto"
                >
                  <option value="">O que você vai enviar?</option>
                  {CATEGORIAS.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
              <div className="simx__field">
                <span className="simx__label">Valor da mercadoria</span>
                <CampoUnidade valor={valorMerc} setValor={setValorMerc} unidade="US$" placeholder="0,00" rotulo="Valor da mercadoria em dólar" />
              </div>
            </div>

            {cobraPorVolume && (
              <p className="simx__notice">
                <Info size={15} />
                Pelas medidas, a cobrança será por volume: {pesoCobrado.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} kg.
              </p>
            )}

            <button type="submit" className="simx__submit" disabled={!podeSimular}>
              {loading ? (
                <>
                  <span className="simx-spinner" /> Consultando FedEx e UPS...
                </>
              ) : (
                <>
                  Ver preços <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="simx__results" aria-live="polite">
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div key="loading" className="simx__list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  {[0, 1].map((i) => (
                    <div key={i} className="simx-offer simx-offer--skeleton">
                      <div className="simx-skel simx-skel--logo" />
                      <div className="simx-skel simx-skel--line" />
                      <div className="simx-skel simx-skel--price" />
                    </div>
                  ))}
                </motion.div>
              ) : erro ? (
                <motion.div key="erro" className="simx__empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="simx__empty-icon simx__empty-icon--erro"><Info size={22} /></div>
                  <p className="simx__empty-title">Não deu para cotar agora</p>
                  <p className="simx__empty-text">{erro}</p>
                </motion.div>
              ) : opcoes && opcoes.length ? (
                <motion.div key="ok" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                  <div className="simx__results-head">
                    <span>
                      <Bandeira iso={destino} /> {PAISES[destino]} · {pesoCobrado.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} kg
                    </span>
                    {!!melhorEconomia && <span className="simx__save-pill">até {melhorEconomia}% off</span>}
                  </div>

                  <div className="simx__list">
                    {[...opcoes].sort((a, b) => totalDe(a) - totalDe(b)).map((o, i) => {
                      const logo = LOGOS[o.carrier.toLowerCase()];
                      const pub = o.published ?? o.price;
                      const economia = pub > o.price ? pub - o.price : 0;
                      const total = totalDe(o);
                      const desemb = Number(o.desembaraco_usd) || 0;
                      const barato = opcoes.length > 1 && total === maisBarato;
                      const rapido = opcoes.length > 1 && o.sla_days === maisRapido && !barato;
                      return (
                        <motion.div
                          key={o.carrier}
                          className={`simx-offer ${barato || opcoes.length === 1 ? "simx-offer--best" : ""}`}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.08 * i, duration: 0.3 }}
                        >
                          <div className="simx-offer__brand">
                            {logo ? (
                              <img src={logo.src} alt={o.carrier} className={`simx-logo ${logo.classe}`} />
                            ) : (
                              <strong>{o.carrier}</strong>
                            )}
                            <div className="simx-offer__meta">
                              <span className="simx-offer__service">{o.service || "Internacional"}</span>
                              {o.sla_days ? (
                                <span className="simx-offer__sla"><Clock size={13} /> ~{o.sla_days} dias úteis</span>
                              ) : null}
                            </div>
                          </div>
                          <div className="simx-offer__price">
                            {barato && <span className="simx-tag simx-tag--best">Mais barato</span>}
                            {rapido && <span className="simx-tag">Mais rápido</span>}
                            {economia > 0 && <span className="simx-offer__list">{usd(pub + extras(o))}</span>}
                            <span className="simx-offer__amount">{usd(total)}</span>
                            <span className="simx-offer__parts">
                              Frete {usd(o.price)}
                              {desemb > 0 && <> · Desembaraço {usd(desemb)}</>}
                              {imposto.status === "ok" && <> · Impostos {usd(imposto.valor)}</>}
                            </span>
                            {economia > 0 && <span className="simx-offer__saving">Você economiza {usd(economia)}</span>}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  <p className={`simx__notice simx__notice--imposto ${imposto.status === "ok" ? "is-ok" : ""}`}>
                    {imposto.status === "loading" ? <span className="simx-spinner simx-spinner--dark" /> : <Info size={15} />}
                    <span>
                      {imposto.status === "off" &&
                        "Informe o tipo de produto e o valor da mercadoria para incluir os impostos do destino no total."}
                      {imposto.status === "loading" &&
                        "Calculando os impostos do destino. A primeira consulta de um produto pode levar até um minuto."}
                      {imposto.status === "ok" &&
                        (imposto.deMinimis
                          ? "Sem imposto de importação no destino para esse valor."
                          : `Impostos estimados no destino: ${usd(imposto.valor)} (alíquota de ~${imposto.aliquota.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%), já somados no total.`)}
                      {imposto.status === "indisponivel" &&
                        `Ainda não estimamos impostos para ${PAISES[destino]} no simulador. O total acima é frete + desembaraço; fale com a gente para calcular o imposto.`}
                      {imposto.status === "erro" &&
                        "Não deu para estimar os impostos agora. O total acima é frete + desembaraço."}
                    </span>
                  </p>

                  <div className="simx__ctas">
                    <a
                      className="simx__cta"
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => onContato("Simulador Resultado")}
                    >
                      Quero enviar com esse preço <ArrowRight size={18} />
                    </a>
                    <a className="simx__cta-sec" href="/login">Já sou cliente</a>
                  </div>
                </motion.div>
              ) : opcoes ? (
                <motion.div key="vazio" className="simx__empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="simx__empty-icon simx__empty-icon--erro"><Info size={22} /></div>
                  <p className="simx__empty-title">Sem tarifa para esse envio</p>
                  <p className="simx__empty-text">Fale com a gente que cotamos manualmente.</p>
                </motion.div>
              ) : (
                <motion.div key="inicio" className="simx__empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="simx__empty-icon"><Package size={24} /></div>
                  <p className="simx__empty-title">Seu comparativo aparece aqui</p>
                  <p className="simx__empty-text">Escolha o destino e o peso para ver o preço da FedEx e da UPS lado a lado.</p>
                  <ul className="simx__perks">
                    <li><Check size={15} /> Tarifas negociadas, não tabela pública</li>
                    <li><Check size={15} /> Mais de 200 países atendidos</li>
                    <li><Check size={15} /> Etiqueta e invoice geradas na plataforma</li>
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <p className="simx__disclaimer">
          Valores em dólar para um volume, estimados na hora com as transportadoras. O total inclui o desembaraço da transportadora e, quando você informa o produto, a estimativa de impostos do destino, que pode variar na alfândega.
        </p>
      </div>
    </section>
  );
}
